/**
 * PremiumField.jsx
 *
 * Campo genérico que renderiza o componente correto baseado no tipo.
 * Centraliza a lógica de seleção de componentes e estilos.
 */

import React, { forwardRef, useMemo } from 'react';
import {
  TextField,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Checkbox,
  Switch,
  InputAdornment,
  IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { usePremiumTokens } from '../../../theme/usePremiumTokens';
import { useTranslation } from '../../LocalizationProvider';
import { useFormTokens, getInputStyles } from '../styles/formTokens';

// Importa componentes especializados existentes
import PremiumSelect from '../PremiumSelect';
import PremiumPhoneInput from '../PremiumPhoneInput';
import PremiumPlateInput from '../PremiumPlateInput';
import PremiumIMEIInput from '../PremiumIMEIInput';
import PremiumCPFCNPJInput from '../PremiumCPFCNPJInput';

/**
 * PremiumField - Renderiza o campo correto baseado no tipo
 *
 * @param {string} type - Tipo do campo
 * @param {string} name - Nome/key do campo
 * @param {any} value - Valor atual
 * @param {Function} onChange - Handler de mudança
 * @param {string} label - Label do campo
 * @param {string} labelKey - Key de tradução para label
 * @param {boolean} required - Se é obrigatório
 * @param {boolean} disabled - Se está desabilitado
 * @param {string} error - Mensagem de erro
 * @param {string} helperText - Texto de ajuda
 * @param {Array} options - Opções para select
 * @param {string} endpoint - Endpoint para select async
 * @param {boolean} multiple - Multi-select
 * @param {Object} sx - Estilos adicionais
 */
const PremiumField = forwardRef(({
  type = 'text',
  name,
  value,
  onChange,
  label,
  labelKey,
  required = false,
  disabled = false,
  error,
  helperText,
  options,
  endpoint,
  multiple = false,
  rows = 4,
  min,
  max,
  step,
  placeholder,
  autoFocus = false,
  sx,
  ...props
}, ref) => {
  const tokens = usePremiumTokens();
  const formTokens = useFormTokens();
  const t = useTranslation();

  // Estado para password visibility
  const [showPassword, setShowPassword] = React.useState(false);

  // Label traduzida
  const fieldLabel = useMemo(() => {
    if (label) return label;
    if (labelKey) return t(labelKey);
    return '';
  }, [label, labelKey, t]);

  // Estilos de input baseados no estado
  const inputStyles = useMemo(
    () => getInputStyles(formTokens, { error: !!error, disabled }),
    [formTokens, error, disabled],
  );

  // Handler genérico de mudança
  const handleChange = (event) => {
    if (!onChange) return;

    // Para checkboxes e switches
    if (type === 'checkbox' || type === 'switch') {
      onChange(event.target.checked);
      return;
    }

    // Para selects
    if (type === 'select') {
      onChange(event);
      return;
    }

    // Para inputs normais
    const newValue = event.target.value;

    // Conversão de tipo para números
    if (type === 'number') {
      const numValue = newValue === '' ? '' : Number(newValue);
      onChange(numValue);
      return;
    }

    onChange(newValue);
  };

  // Renderiza Checkbox
  if (type === 'checkbox') {
    return (
      <FormControl error={!!error} disabled={disabled} sx={sx}>
        <FormControlLabel
          control={
            <Checkbox
              ref={ref}
              name={name}
              checked={Boolean(value)}
              onChange={handleChange}
              disabled={disabled}
              sx={{
                color: tokens?.border?.default,
                '&.Mui-checked': {
                  color: tokens?.accent?.primary,
                },
              }}
            />
          }
          label={fieldLabel}
          sx={{
            color: tokens?.text?.primary,
            '& .MuiFormControlLabel-label': {
              fontSize: '0.875rem',
            },
          }}
        />
        {(error || helperText) && (
          <FormHelperText>{error || helperText}</FormHelperText>
        )}
      </FormControl>
    );
  }

  // Renderiza Switch
  if (type === 'switch') {
    return (
      <FormControl error={!!error} disabled={disabled} sx={sx}>
        <FormControlLabel
          control={
            <Switch
              ref={ref}
              name={name}
              checked={Boolean(value)}
              onChange={handleChange}
              disabled={disabled}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: tokens?.accent?.primary,
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: tokens?.accent?.primary,
                },
              }}
            />
          }
          label={fieldLabel}
          sx={{
            color: tokens?.text?.primary,
            '& .MuiFormControlLabel-label': {
              fontSize: '0.875rem',
            },
          }}
        />
        {(error || helperText) && (
          <FormHelperText>{error || helperText}</FormHelperText>
        )}
      </FormControl>
    );
  }

  // Renderiza Select
  if (type === 'select') {
    return (
      <PremiumSelect
        ref={ref}
        label={fieldLabel}
        value={value}
        onChange={handleChange}
        options={options || []}
        multiple={multiple}
        required={required}
        disabled={disabled}
        error={!!error}
        helperText={error || helperText}
        fullWidth
        sx={sx}
        {...props}
      />
    );
  }

  // Renderiza Phone Input
  if (type === 'phone' || type === 'tel') {
    return (
      <PremiumPhoneInput
        ref={ref}
        label={fieldLabel}
        value={value || ''}
        onChange={onChange}
        required={required}
        disabled={disabled}
        error={!!error}
        helperText={error || helperText}
        sx={sx}
        {...props}
      />
    );
  }

  // Renderiza Plate Input
  if (type === 'plate') {
    return (
      <PremiumPlateInput
        ref={ref}
        label={fieldLabel}
        value={value || ''}
        onChange={onChange}
        required={required}
        disabled={disabled}
        error={!!error}
        helperText={error || helperText}
        sx={sx}
        {...props}
      />
    );
  }

  // Renderiza IMEI Input
  if (type === 'imei') {
    return (
      <PremiumIMEIInput
        ref={ref}
        label={fieldLabel}
        value={value || ''}
        onChange={onChange}
        required={required}
        disabled={disabled}
        error={!!error}
        helperText={error || helperText}
        sx={sx}
        {...props}
      />
    );
  }

  // Renderiza CPF/CNPJ Input
  if (type === 'cpf' || type === 'cnpj' || type === 'cpfcnpj') {
    return (
      <PremiumCPFCNPJInput
        ref={ref}
        label={fieldLabel}
        value={value || ''}
        onChange={onChange}
        required={required}
        disabled={disabled}
        error={!!error}
        helperText={error || helperText}
        allowBoth={type === 'cpfcnpj'}
        sx={sx}
        {...props}
      />
    );
  }

  // Renderiza Password Input
  if (type === 'password') {
    return (
      <TextField
        ref={ref}
        name={name}
        type={showPassword ? 'text' : 'password'}
        value={value || ''}
        onChange={handleChange}
        label={fieldLabel}
        required={required}
        disabled={disabled}
        error={!!error}
        helperText={error || helperText}
        placeholder={placeholder}
        autoFocus={autoFocus}
        fullWidth
        size="small"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
                size="small"
                sx={{ color: tokens?.text?.muted }}
              >
                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          ...inputStyles,
          ...sx,
        }}
        {...props}
      />
    );
  }

  // Renderiza Textarea (multiline)
  if (type === 'textarea' || type === 'multiline') {
    return (
      <TextField
        ref={ref}
        name={name}
        value={value || ''}
        onChange={handleChange}
        label={fieldLabel}
        required={required}
        disabled={disabled}
        error={!!error}
        helperText={error || helperText}
        placeholder={placeholder}
        autoFocus={autoFocus}
        fullWidth
        size="small"
        multiline
        rows={rows}
        sx={{
          ...inputStyles,
          ...sx,
        }}
        {...props}
      />
    );
  }

  // Renderiza Date Input
  if (type === 'date') {
    return (
      <TextField
        ref={ref}
        name={name}
        type="date"
        value={value || ''}
        onChange={handleChange}
        label={fieldLabel}
        required={required}
        disabled={disabled}
        error={!!error}
        helperText={error || helperText}
        autoFocus={autoFocus}
        fullWidth
        size="small"
        InputLabelProps={{
          shrink: true,
        }}
        sx={{
          ...inputStyles,
          ...sx,
        }}
        {...props}
      />
    );
  }

  // Renderiza DateTime Input
  if (type === 'datetime' || type === 'datetime-local') {
    return (
      <TextField
        ref={ref}
        name={name}
        type="datetime-local"
        value={value || ''}
        onChange={handleChange}
        label={fieldLabel}
        required={required}
        disabled={disabled}
        error={!!error}
        helperText={error || helperText}
        autoFocus={autoFocus}
        fullWidth
        size="small"
        InputLabelProps={{
          shrink: true,
        }}
        sx={{
          ...inputStyles,
          ...sx,
        }}
        {...props}
      />
    );
  }

  // Renderiza Time Input
  if (type === 'time') {
    return (
      <TextField
        ref={ref}
        name={name}
        type="time"
        value={value || ''}
        onChange={handleChange}
        label={fieldLabel}
        required={required}
        disabled={disabled}
        error={!!error}
        helperText={error || helperText}
        autoFocus={autoFocus}
        fullWidth
        size="small"
        InputLabelProps={{
          shrink: true,
        }}
        sx={{
          ...inputStyles,
          ...sx,
        }}
        {...props}
      />
    );
  }

  // Renderiza Number Input
  if (type === 'number') {
    return (
      <TextField
        ref={ref}
        name={name}
        type="number"
        value={value ?? ''}
        onChange={handleChange}
        label={fieldLabel}
        required={required}
        disabled={disabled}
        error={!!error}
        helperText={error || helperText}
        placeholder={placeholder}
        autoFocus={autoFocus}
        fullWidth
        size="small"
        inputProps={{
          min,
          max,
          step,
        }}
        sx={{
          ...inputStyles,
          ...sx,
        }}
        {...props}
      />
    );
  }

  // Renderiza Email Input
  if (type === 'email') {
    return (
      <TextField
        ref={ref}
        name={name}
        type="email"
        value={value || ''}
        onChange={handleChange}
        label={fieldLabel}
        required={required}
        disabled={disabled}
        error={!!error}
        helperText={error || helperText}
        placeholder={placeholder}
        autoFocus={autoFocus}
        fullWidth
        size="small"
        sx={{
          ...inputStyles,
          ...sx,
        }}
        {...props}
      />
    );
  }

  // Renderiza URL Input
  if (type === 'url') {
    return (
      <TextField
        ref={ref}
        name={name}
        type="url"
        value={value || ''}
        onChange={handleChange}
        label={fieldLabel}
        required={required}
        disabled={disabled}
        error={!!error}
        helperText={error || helperText}
        placeholder={placeholder || 'https://'}
        autoFocus={autoFocus}
        fullWidth
        size="small"
        sx={{
          ...inputStyles,
          ...sx,
        }}
        {...props}
      />
    );
  }

  // Renderiza Text Input (default)
  return (
    <TextField
      ref={ref}
      name={name}
      type="text"
      value={value || ''}
      onChange={handleChange}
      label={fieldLabel}
      required={required}
      disabled={disabled}
      error={!!error}
      helperText={error || helperText}
      placeholder={placeholder}
      autoFocus={autoFocus}
      fullWidth
      size="small"
      sx={{
        ...inputStyles,
        ...sx,
      }}
      {...props}
    />
  );
});

PremiumField.displayName = 'PremiumField';

export default PremiumField;
